/**
 * FileMenu — Document I/O ribbon entry.
 *
 * Open / Save / Export the active document. Live capabilities track
 * the engine surface:
 *
 *   - **Open** → hidden file input → `Command::OpenDocument`. Issue #339 —
 *     `.docx`, `.txt` and `.html` / `.htm` are all engine-real; the format
 *     comes from the file name (`docFormatForFileName`), so no entry here
 *     needs an "Engine pending" badge.
 *   - **Save** → `Command::SaveDocument { format: Docx }`. Bound to
 *     `Ctrl/Cmd+S` globally.
 *   - **Export PDF** → `Command::ExportPdf { conformance }`. All four
 *     conformance targets are engine-real: `A1b` → PDF/A-1b, `A2u` →
 *     PDF/A-2u, `X3` → PDF/X-3:2003 (issue #28 closed the former
 *     `PdfProfile::Plain` fallback that kept A2u / X3 gated), `Ua1` →
 *     PDF/UA-1 (issue #360): the tagged, accessible export — structure
 *     tree, reading order, `/Lang`, figure alt text — on a PDF/A-2u base.
 *     `A2u` itself stays untagged.
 *   - **Export HTML / Plain Text** → `Command::SaveDocument { format }`
 *     with `html` / `plain_text`. Sprint 9 wired the engine
 *     serializers (`crates/format-html` + `DocumentTree::to_plain_text`);
 *     bytes flow back through `DOCUMENT_SAVED` and the auto-download
 *     subscriber picks the right MIME + extension.
 *
 * Issue #345 — an encrypted (password-protected) `.docx` picked here
 * answers `ERROR { kind: 'EncryptedDocument' }`; the menu then asks for
 * the password in a dialog and re-opens the file with it
 * (`openDocument(…, { password })`), saying so on a wrong one. The
 * password is never stored, and the dialog says that a save writes the
 * document WITHOUT one (writing encrypted packages is not supported).
 *
 * Download trigger: subscribes to `DOCUMENT_SAVED` / `PDF_EXPORTED`
 * events, wraps the returned `bytes` in a `Blob`, and synthesises a
 * download anchor click. The active document name (set by the most
 * recent Open) drives the default filename.
 */
import {
    createEffect,
    createSignal,
    onCleanup,
    Show,
    type Component,
} from 'solid-js';
import {
    createEditorCommands,
    useEngine,
    type Event,
    type PdfConformance,
} from '@nge/core';
import { Dialog } from './Dialog';
import { openErrorMessage } from './openErrorCopy';
import './FileMenu.css';

/** Issue #339 — every format `OpenDocument` implements (see
 *  `docFormatForFileName` in @nge/core for the extension mapping). */
const OPEN_ACCEPT = [
    '.docx',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.txt',
    'text/plain',
    '.html',
    '.htm',
    'text/html',
].join(',');

const PDF_CONFORMANCES: {
    value: PdfConformance;
    label: string;
    hint: string;
}[] = [
    { value: 'A1b', label: 'PDF/A-1b', hint: 'Archival (default)' },
    { value: 'A2u', label: 'PDF/A-2u', hint: 'Archival, Unicode' },
    { value: 'X3', label: 'PDF/X-3', hint: 'Print-ready' },
    { value: 'Ua1', label: 'PDF/UA-1', hint: 'Accessible (tagged), archival' },
];

export interface FileMenuProps {
    /** Default filename stem when the active document has no name. */
    defaultFilename?: string;
    /** Whether to bind `Ctrl/Cmd+S` to Save. Default true. */
    bindShortcuts?: boolean;
}

function triggerDownload(bytes: Uint8Array, mime: string, filename: string): void {
    const blob = new Blob([bytes as BlobPart], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    /* Defer revoke so Safari has time to start the download. */
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* The banner text for an engine `ERROR` lives in `openErrorCopy.ts`
 * (`openErrorMessage`, issue #491: pure, unit-tested). */

export const FileMenu: Component<FileMenuProps> = (props) => {
    const cmd = createEditorCommands();
    const engine = useEngine();
    const [open, setOpen] = createSignal(false);
    const [exportOpen, setExportOpen] = createSignal(false);
    const [pdfPickerOpen, setPdfPickerOpen] = createSignal(false);
    const [docName, setDocName] = createSignal<string | undefined>(undefined);
    const [error, setError] = createSignal<string | null>(null);
    let fileInput: HTMLInputElement | undefined;
    /* Issue #345 — the encrypted file waiting for its password. The File
     * itself is kept: `openDocument` TRANSFERS its bytes, so each attempt
     * re-reads them. */
    const [locked, setLocked] = createSignal<File | null>(null);
    const [password, setPassword] = createSignal('');
    const [passwordError, setPasswordError] = createSignal<string | null>(null);
    const [unlocking, setUnlocking] = createSignal(false);
    /* While an open from the picker / the password dialog is in flight,
     * its encryption errors belong to the dialog, not the banner. */
    let dialogOwnsErrors = false;

    const stem = () => {
        const name = docName() ?? props.defaultFilename ?? 'document';
        return name.replace(/\.[^.]+$/, '');
    };

    /* Subscribe to engine events so saves + exports auto-download.
     * `Event::Error` from any DocFormat path bubbles into the error
     * banner. Sprint 9 — DocFormat::Html / PlainText share the same
     * DOCUMENT_SAVED reply; `pendingExportFormat` (set by the export
     * handler) tells us which MIME + extension to pick. */
    createEffect(() => {
        const unsub = engine.subscribe((evt) => {
            switch (evt.type) {
                case 'DOCUMENT_SAVED': {
                    const pending = pendingExportFormat();
                    if (pending === 'html') {
                        triggerDownload(evt.bytes, 'text/html;charset=utf-8', `${stem()}.html`);
                    } else if (pending === 'plain_text') {
                        triggerDownload(evt.bytes, 'text/plain;charset=utf-8', `${stem()}.txt`);
                    } else {
                        triggerDownload(
                            evt.bytes,
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                            `${stem()}.docx`,
                        );
                    }
                    setPendingExportFormat(null);
                    break;
                }
                case 'PDF_EXPORTED':
                    triggerDownload(evt.bytes, 'application/pdf', `${stem()}.pdf`);
                    break;
                case 'ERROR':
                    /* Issue #348 — a refused open (the package exceeded
                       the reader's resource limits) says so plainly; the
                       previous document stays open. Issue #345 — so does
                       an encrypted package opened through the API (an
                       open from File → Open prompts for the password
                       instead, and its errors belong to that dialog); a
                       protection refusal is toasted by the ErrorToast
                       (engine copy), not here. */
                    if (evt.kind === 'Protected') break;
                    if (
                        dialogOwnsErrors &&
                        (evt.kind === 'EncryptedDocument' || evt.kind === 'WrongPassword')
                    ) {
                        break;
                    }
                    setError(openErrorMessage(evt.kind, evt.message));
                    setPendingExportFormat(null);
                    setTimeout(() => setError(null), 6000);
                    break;
            }
        });
        onCleanup(unsub);
    });

    const closeAll = () => {
        setOpen(false);
        setExportOpen(false);
        setPdfPickerOpen(false);
    };

    /* Click-away dismiss. */
    createEffect(() => {
        if (!open()) return;
        const handler = (e: MouseEvent) => {
            const root = (e.target as HTMLElement | null)?.closest('.nge-fm');
            if (!root) closeAll();
        };
        window.addEventListener('mousedown', handler);
        onCleanup(() => window.removeEventListener('mousedown', handler));
    });

    /* Ctrl/Cmd+S binding. */
    createEffect(() => {
        if (props.bindShortcuts === false) return;
        const handler = (e: KeyboardEvent) => {
            const ctrl = e.ctrlKey || e.metaKey;
            if (ctrl && !e.shiftKey && e.key.toLowerCase() === 's') {
                e.preventDefault();
                void cmd.saveDocument('docx');
            }
        };
        window.addEventListener('keydown', handler);
        onCleanup(() => window.removeEventListener('keydown', handler));
    });

    /** Open `file` (with `pw` when given); the reply. */
    const openFile = async (file: File, pw?: string): Promise<Event> => {
        const bytes = new Uint8Array(await file.arrayBuffer());
        dialogOwnsErrors = true;
        try {
            return await cmd.openDocument(
                bytes,
                file.name,
                pw === undefined ? undefined : { password: pw },
            );
        } finally {
            dialogOwnsErrors = false;
        }
    };

    const onPickFile = async (file: File) => {
        setDocName(file.name);
        const evt = await openFile(file);
        closeAll();
        if (fileInput) fileInput.value = '';
        /* Issue #345 — encrypted: ask for the password. */
        if (evt.type === 'ERROR' && evt.kind === 'EncryptedDocument') {
            setPassword('');
            setPasswordError(null);
            setLocked(file);
        }
    };

    const unlock = async () => {
        const file = locked();
        if (!file || unlocking() || password() === '') return;
        setUnlocking(true);
        let evt: Event;
        try {
            evt = await openFile(file, password());
        } finally {
            setUnlocking(false);
        }
        if (evt.type === 'ERROR') {
            setPasswordError(
                evt.kind === 'WrongPassword'
                    ? 'That password is incorrect. Try again.'
                    : evt.message,
            );
            return;
        }
        setPassword('');
        setLocked(null);
    };

    const onSave = async () => {
        closeAll();
        await cmd.saveDocument('docx');
    };

    const onExportPdf = async (conformance: PdfConformance) => {
        closeAll();
        await cmd.exportPdf(conformance);
    };

    /* Sprint 9 — HTML / Plain Text exports go through the same
     * Command::SaveDocument path; the DOCUMENT_SAVED subscriber above
     * triggers the download. Track which format is in flight so the
     * download wrapper picks the right MIME + extension. */
    const [pendingExportFormat, setPendingExportFormat] = createSignal<
        'html' | 'plain_text' | null
    >(null);

    const onExportHtml = async () => {
        closeAll();
        setPendingExportFormat('html');
        await cmd.saveDocument('html');
    };

    const onExportPlainText = async () => {
        closeAll();
        setPendingExportFormat('plain_text');
        await cmd.saveDocument('plain_text');
    };

    return (
        <>
            <div class="nge-fm">
                <button
                    class="nge-btn nge-fm__trigger"
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={open()}
                    onClick={() => setOpen((v) => !v)}
                >
                    <span>File</span>
                    <span class="nge-fm__chevron" aria-hidden="true">▾</span>
                </button>

                <Show when={open()}>
                    <ul class="nge-fm__menu" role="menu">
                        <li role="none">
                            <button
                                class="nge-fm__item"
                                type="button"
                                role="menuitem"
                                data-nge-command="OPEN_DOCUMENT"
                                onClick={() => fileInput?.click()}
                            >
                                <span>Open…</span>
                                <span class="nge-fm__shortcut">.docx · .txt · .html</span>
                            </button>
                        </li>
                        <li role="none">
                            <button
                                class="nge-fm__item"
                                type="button"
                                role="menuitem"
                                data-nge-command="SAVE_DOCUMENT"
                                onClick={() => void onSave()}
                            >
                                <span>Save</span>
                                <span class="nge-fm__shortcut">Ctrl+S</span>
                            </button>
                        </li>
                        <li class="nge-fm__separator" role="separator" />
                        <li role="none" class="nge-fm__submenu-anchor">
                            <button
                                class="nge-fm__item"
                                type="button"
                                role="menuitem"
                                aria-haspopup="menu"
                                aria-expanded={exportOpen()}
                                onClick={() => setExportOpen((v) => !v)}
                            >
                                <span>Export</span>
                                <span class="nge-fm__submenu-arrow" aria-hidden="true">▸</span>
                            </button>
                            <Show when={exportOpen()}>
                                <ul class="nge-fm__menu nge-fm__submenu" role="menu">
                                    <li role="none">
                                        <button
                                            class="nge-fm__item"
                                            type="button"
                                            role="menuitem"
                                            onClick={() => setPdfPickerOpen((v) => !v)}
                                            aria-haspopup="menu"
                                            aria-expanded={pdfPickerOpen()}
                                        >
                                            <span>PDF…</span>
                                            <span class="nge-fm__submenu-arrow" aria-hidden="true">▸</span>
                                        </button>
                                        <Show when={pdfPickerOpen()}>
                                            <ul class="nge-fm__menu nge-fm__submenu" role="menu">
                                                {PDF_CONFORMANCES.map((c) => (
                                                    <li role="none">
                                                        <button
                                                            class="nge-fm__item nge-fm__item--stacked"
                                                            type="button"
                                                            role="menuitem"
                                                            data-nge-command="EXPORT_PDF"
                                                            title={c.hint}
                                                            onClick={() => void onExportPdf(c.value)}
                                                        >
                                                            <span class="nge-fm__label">{c.label}</span>
                                                            <span class="nge-fm__hint">{c.hint}</span>
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </Show>
                                    </li>
                                    <li role="none">
                                        <button
                                            class="nge-fm__item"
                                            type="button"
                                            role="menuitem"
                                            data-nge-command="SAVE_DOCUMENT"
                                            onClick={() => void onExportHtml()}
                                        >
                                            <span>HTML</span>
                                            <span class="nge-fm__shortcut">.html</span>
                                        </button>
                                    </li>
                                    <li role="none">
                                        <button
                                            class="nge-fm__item"
                                            type="button"
                                            role="menuitem"
                                            data-nge-command="SAVE_DOCUMENT"
                                            onClick={() => void onExportPlainText()}
                                        >
                                            <span>Plain Text</span>
                                            <span class="nge-fm__shortcut">.txt</span>
                                        </button>
                                    </li>
                                </ul>
                            </Show>
                        </li>
                    </ul>
                </Show>

                <input
                    ref={(el) => (fileInput = el)}
                    type="file"
                    accept={OPEN_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const file = e.currentTarget.files?.[0];
                        if (file) void onPickFile(file);
                    }}
                />
            </div>

            <Show when={error()}>
                <div class="nge-fm__error" role="alert">{error()}</div>
            </Show>

            {/* Issue #345 — the password prompt for an encrypted file. */}
            <Dialog
                open={locked() !== null}
                title="Password required"
                size="sm"
                onClose={() => setLocked(null)}
                footer={
                    <>
                        <button class="nge-btn" type="button" onClick={() => setLocked(null)}>
                            Cancel
                        </button>
                        <button
                            class="nge-btn nge-btn--primary nge-fm__unlock"
                            type="button"
                            disabled={unlocking() || password() === ''}
                            onClick={() => void unlock()}
                        >
                            Open
                        </button>
                    </>
                }
            >
                <form
                    class="nge-fm__pw"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void unlock();
                    }}
                >
                    <p class="nge-fm__pw-text">
                        “{locked()?.name}” is encrypted. Enter its password to open it.
                    </p>
                    <input
                        class="nge-fm__pw-input"
                        type="password"
                        autofocus
                        autocomplete="off"
                        aria-label="Document password"
                        value={password()}
                        onInput={(e) => {
                            setPassword(e.currentTarget.value);
                            setPasswordError(null);
                        }}
                    />
                    <Show when={passwordError()}>
                        <p class="nge-fm__pw-error" role="alert">
                            {passwordError()}
                        </p>
                    </Show>
                    <p class="nge-fm__pw-note">
                        The password is not stored. Saving writes this document without a
                        password.
                    </p>
                </form>
            </Dialog>
        </>
    );
};
